<template>
  <div class="custom-form-container">
    <div class="container pt-4 mt-4">
      <div class="row justify-content-center mt-3">
        <div class="col-md-8">
          <div class="card">
            <div class="card-header">
              <h3 class="card-title">{{ $i18n.locale === 'es' ? form.name_es : form.name }}</h3>
              <p class="card-description">{{ $i18n.locale === 'es' ? form.description_es : form.description }}</p>
            </div>
            <div class="card-body">
              <form @submit.prevent="handleSubmit">
                <div v-for="(field, index) in form.fields" :key="index" class="form-group">
                  <label :for="'field-' + index" class="form-label">
                    {{ $i18n.locale === 'es' ? field.label_es : field.label }}
                    <span v-if="field.required" class="required">*</span>
                  </label>
                  <p v-if="field.description" class="field-description">
                    {{ $i18n.locale === 'es' ? field.description_es : field.description }}
                  </p>
                  <input
                    v-if="field.type !== 'select'"
                    :type="field.type"
                    :id="'field-' + index"
                    class="form-control"
                    v-bind="field.required ? { required: true } : {}"
                    :value="field.type === 'file' ? '' : formData[index]"
                    @input="field.type !== 'file' && (formData[index] = $event.target.value)"
                    :placeholder="$i18n.locale === 'es' ? field.label_es : field.label"
                    @change="field.type === 'file' ? handleFileChange($event, index) : null"
                  >
                  <select
                    v-else
                    :id="'field-' + index"
                    class="form-control"
                    v-bind="field.required ? { required: true } : {}"
                    v-model="formData[index]"
                  >
                    <option
                      v-for="(option, optionIndex) in field.options"
                      :key="optionIndex"
                      :value="$i18n.locale === 'es' ? option.label_es : option.label"
                    >
                      {{ $i18n.locale === 'es' ? option.label_es : option.label }}
                    </option>
                  </select>
                </div>
                <div class="form-actions">
                  <button :disabled="saving" type="submit" class="btn btn-primary">
                    <i class="fa fa-spinner fa-spin" v-if="saving"></i>
                    <span v-else>{{ $t('submit') }}</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import formService from '@/services/form'
import uploadService from '@/services/upload'

export default {
  name: 'CustomForm',
  data () {
    return {
      saving: false,
      form: {
        _id: '',
        name: '',
        description: '',
        fields: []
      },
      formData: [],
      fileInputs: {}
    }
  },
  methods: {
    handleFileChange (event, index) {
      const file = event.target.files[0]

      if (file) {
        this.fileInputs[index] = file
        this.formData[index] = file.name
      } else {
        this.fileInputs[index] = null
        this.formData[index] = ''
      }
    },
    async fetchForm () {
      try {
        const response = await formService.get(this.$route.params.id)
        this.form = response.data
        this.formData = new Array(this.form.fields.length).fill('')
      } catch (error) {
        console.error('Error fetching form:', error)
      }
    },
    async handleSubmit () {
      this.saving = true

      try {
        // Process each field and handle file uploads if needed
        const processedFields = await Promise.all(
          this.form.fields.map(async (field, index) => {
            const value = this.formData[index]

            // If the field is a file type and has a value, upload it
            if (field.type === 'file' && this.fileInputs[index]) {
              const fileResponse = await uploadService.uploadFormFile(this.fileInputs[index])
              return {
                label: field.label,
                value: fileResponse.data
              }
            }

            // For non-file fields, return as is
            return {
              label: field.label,
              value: value
            }
          })
        )

        await formService.submit(this.form._id, {
          fields: processedFields
        })

        this.$notify({ message: 'Form submitted successfully' }, 'success', 'fa-check')
        this.$router.push('/dashboard')
      } catch (error) {
        this.saving = false
        console.error('Error submitting form:', error)
        this.$notify({ message: 'There was an error submitting the form' }, 'error', 'fa-exclamation')
      }
    }
  },
  created () {
    this.fetchForm()
  }
}
</script>

<style lang="scss" scoped>
.custom-form-container {
  padding: 2rem 0;
  min-height: 100vh;
  background-color: #f8f9fa;
}

.card {
  border: none;
  border-radius: 8px;
  box-shadow: 0 0.75rem 1.5rem rgba(18, 38, 63, 0.03);
  background: #fff;
}

.card-header {
  background: none;
  border-bottom: 1px solid #ebecec;
  padding: 1.5rem;
}

.card-title {
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: #1a2035;
}

.card-description {
  color: #6c757d;
  margin-bottom: 0;
}

.card-body {
  padding: 1.5rem;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-label {
  font-weight: 500;
  color: #495057;
  margin-bottom: 0.5rem;
  display: block;
}

.required {
  color: #f25961;
  margin-left: 0.25rem;
}

.field-description {
  font-size: 0.875rem;
  color: #6c757d;
  margin-bottom: 0.5rem;
}

.form-control {
  height: 42px;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  border: 1px solid #ced4da;
  border-radius: 4px;
  transition: all 0.2s ease;

  &:focus {
    border-color: #1572e8;
    box-shadow: 0 0 0 0.2rem rgba(21, 114, 232, 0.25);
  }
}

.form-actions {
  margin-top: 2rem;
  text-align: right;
}

.btn-primary {
  background-color: #1572e8;
  border-color: #1572e8;
  padding: 0.5rem 1.5rem;
  font-weight: 500;
  transition: all 0.2s ease;

  &:hover {
    background-color: #0f6dad;
    border-color: #0f6dad;
  }
}

@media (max-width: 768px) {
  .custom-form-container {
    padding: 1rem 0;
  }

  .card-header {
    padding: 1rem;
  }

  .card-body {
    padding: 1rem;
  }

  .form-actions {
    text-align: center;
  }

  .btn-primary {
    width: 100%;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/CustomForm.vue